import axios from "axios";

export type Unit = {
  flat_id: string;
  unit: string;
  price: number | null;
  area: number;
  status: string;
  price_psf: number | null;
  plan: string;
  type: string;
  type_feature: string | null;
};

type RangeValue = number | null;

export type AvailabilitySummary = {
  total: number;
  available: number;
  sold: number;
  reserved: number;
  minPrice: RangeValue;
  maxPrice: RangeValue;
  minPsf: RangeValue;
  maxPsf: RangeValue;
  lastUpdated?: undefined;
  byType: Array<{
    type: string;
    total: number;
    available: number;
    minPrice: RangeValue;
    maxPrice: RangeValue;
    minArea: number;
    maxArea: number;
    minPsf: RangeValue;
    maxPsf: RangeValue;
  }>;
  byPlan: Array<{
    plan: string;
    type: string;
    total: number;
    available: number;
    minArea: number;
    maxArea: number;
    minPrice: RangeValue;
    maxPrice: RangeValue;
  }>;
};

const numericRange = (values: Array<number | null>): [RangeValue, RangeValue] => {
  const present = values.filter((value): value is number => value != null);
  return present.length ? [Math.min(...present), Math.max(...present)] : [null, null];
};

const displayType = (unit: Unit) =>
  unit.type_feature ? `${unit.type} + ${unit.type_feature}` : unit.type;

const isAvailable = (unit: Unit) => unit.status.trim().toLowerCase() === "available";

export async function fetchPriceList(propertyId: string): Promise<Unit[]> {
  try {
    const response = await axios.get(
      `${process.env.NEXT_PUBLIC_API_URL}/property-meta-data/export-pricelist/${propertyId}`
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch {
    return [];
  }
}

export function summarise(units: Unit[]): AvailabilitySummary {
  const typeGroups = new Map<string, Unit[]>();
  const planGroups = new Map<string, Unit[]>();

  units.forEach((unit) => {
    const type = displayType(unit);
    typeGroups.set(type, [...(typeGroups.get(type) ?? []), unit]);
    const planKey = `${type}\u0000${unit.plan}`;
    planGroups.set(planKey, [...(planGroups.get(planKey) ?? []), unit]);
  });

  const prices = units.map((unit) => unit.price);
  const psfs = units.map((unit) => unit.price_psf);
  const [minPrice, maxPrice] = numericRange(prices);
  const [minPsf, maxPsf] = numericRange(psfs);
  const bedroomCount = (value: string) => {
    const match = value.match(/\d+/);
    return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
  };

  const byType = Array.from(typeGroups, ([type, group]) => {
    const [typeMinPrice, typeMaxPrice] = numericRange(group.map((unit) => unit.price));
    const [typeMinPsf, typeMaxPsf] = numericRange(group.map((unit) => unit.price_psf));
    const areas = group.map((unit) => unit.area);
    return {
      type,
      total: group.length,
      available: group.filter(isAvailable).length,
      minPrice: typeMinPrice,
      maxPrice: typeMaxPrice,
      minArea: Math.min(...areas),
      maxArea: Math.max(...areas),
      minPsf: typeMinPsf,
      maxPsf: typeMaxPsf,
    };
  }).sort(
    (a, b) => bedroomCount(a.type) - bedroomCount(b.type) || a.type.localeCompare(b.type)
  );

  const byPlan = Array.from(planGroups, ([key, group]) => {
    const [type, plan] = key.split("\u0000");
    const [planMinPrice, planMaxPrice] = numericRange(group.map((unit) => unit.price));
    const areas = group.map((unit) => unit.area);
    return {
      plan,
      type,
      total: group.length,
      available: group.filter(isAvailable).length,
      minArea: Math.min(...areas),
      maxArea: Math.max(...areas),
      minPrice: planMinPrice,
      maxPrice: planMaxPrice,
    };
  }).sort((a, b) => a.type.localeCompare(b.type) || a.plan.localeCompare(b.plan));

  return {
    total: units.length,
    available: units.filter(isAvailable).length,
    sold: units.filter((unit) => unit.status.trim().toLowerCase() === "sold").length,
    reserved: units.filter((unit) => {
      const status = unit.status.trim().toLowerCase();
      return status !== "available" && status !== "sold";
    }).length,
    minPrice,
    maxPrice,
    minPsf,
    maxPsf,
    byType,
    byPlan,
  };
}

export function floorPlanImageFor(
  plan: string,
  floorImages: string[]
): string | undefined {
  if (!plan) return undefined;
  const planLower = plan.toLowerCase();
  const norm = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
  const planNorm = norm(plan);

  return floorImages.find((file) => {
    const lower = file.toLowerCase();
    const beforeUnderscore = lower.split("_")[0];
    if (beforeUnderscore === planLower) return true;
    const base = lower.replace(/\.[^.]+$/, "");
    if (base === planLower) return true;
    if (!planNorm) return false;
    const lastHyphenToken = base.split("-").pop() || "";
    return (
      norm(base) === planNorm ||
      norm(lastHyphenToken) === planNorm ||
      norm(beforeUnderscore) === planNorm
    );
  });
}
